import { toast } from 'react-toastify';
import { AUTH_SERVICE_URL } from './constant';

class GlobalErrorHandler {
    constructor() {
        this.isHandlingError = false;
        this.reloadCooldownMs = 5 * 60 * 1000; // 5 minutes cooldown to avoid reload loops
        this._lastReportedKey = null;
        this.setupGlobalErrorHandlers();
    }

    setupGlobalErrorHandlers() {
        // Handle uncaught JavaScript errors
        window.addEventListener('error', (event) => {
            console.error('Global error caught:', event.error);
            this.handleGlobalError(event.error, 'JavaScript Error');
        });

        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            console.error('Unhandled promise rejection:', event.reason);
            this.handleGlobalError(event.reason, 'Promise Rejection');
        });

        // Handle resource loading errors (scripts, stylesheets, images)
        window.addEventListener('error', (event) => {
            if (event.target !== window) {
                console.error('Resource loading error:', event.target.src || event.target.href);
                this.handleResourceError(event);
            }
        }, true); // Use capture phase
    }

    handleGlobalError(error, errorType) {
        if (this.isHandlingError) return;

        // Check if this is a deployment-related error
        if (this.isDeploymentRelatedError(error)) {
            console.log(`${errorType} - Deployment-related error detected, reloading...`);
            // Best-effort: report this crash to backend for debugging
            this.reportClientError(error, { errorType });
            this.handleDeploymentError();
        }
    }

    handleResourceError(event) {
        const target = event.target;
        const isScript = target.tagName === 'SCRIPT';
        const isStylesheet = target.tagName === 'LINK' && target.rel === 'stylesheet';
        
        if (isScript || isStylesheet) {
            console.log('Critical resource failed to load, possible deployment update');
            this.reportClientError(
                new Error('Resource loading error'),
                { resource: target.src || target.href || '', tagName: target.tagName }
            );
            this.handleDeploymentError();
        }
    }

    reportClientError(error, extra = {}) {
        try {
            const message = error && (error.message || error.toString && error.toString()) || 'Unknown error';
            const stack = error && error.stack ? String(error.stack) : '';
            const url = window.location && window.location.href ? window.location.href : '';
            const userAgent = navigator && navigator.userAgent ? navigator.userAgent : '';

            let portalId, clientId, username;
            try {
                const params = new URLSearchParams(window.location.search || '');
                portalId = parseInt(params.get('pId') || params.get('portalId') || '0', 10) || 0;
                clientId = parseInt(params.get('cId') || params.get('clientId') || '0', 10) || 0;
                username = params.get('username') || '';
            } catch (e) {
                // ignore
            }

            const appVersion = (() => {
                try {
                    return localStorage.getItem('app-version') || '';
                } catch (e) {
                    return '';
                }
            })();

            const key = `${message}::${url}::${appVersion}`;
            if (this._lastReportedKey === key) return;
            this._lastReportedKey = key;

            const payload = {
                message: String(message),
                stack,
                url,
                userAgent,
                portalId,
                clientId,
                username,
                appVersion,
                extra,
                ts: new Date().toISOString(),
            };

            const endpoint = `${AUTH_SERVICE_URL}/portal/client-error`;

            if (navigator && typeof navigator.sendBeacon === 'function') {
                const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
                navigator.sendBeacon(endpoint, blob);
                return;
            }

            fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: true,
            }).catch(() => { /* ignore */ });
        } catch (e) {
            // ignore
        }
    }

    isDeploymentRelatedError(error) {
        if (!error) return false;

        const errorString = error.toString().toLowerCase();
        const stackString = error.stack ? error.stack.toLowerCase() : '';

        const deploymentErrorPatterns = [
            // React-specific errors
            'removechild',
            'node to be removed is not a child',
            'cannot read property',
            'cannot read properties of null',
            'cannot read properties of undefined',
            
            // Webpack/Module loading errors
            'loading chunk',
            'loading css chunk',
            'chunkloaderror',
            'failed to import',
            'module not found',
            
            // Network/Script errors
            'script error',
            'network error',
            'failed to fetch',
            'unexpected token',
            'syntaxerror',
            
            // Cache-related errors
            'cache',
            'stale',
            
            // Version mismatch indicators
            'version',
            'mismatch'
        ];

        return deploymentErrorPatterns.some(pattern => 
            errorString.includes(pattern) || stackString.includes(pattern)
        );
    }

    handleDeploymentError() {
        if (this.isHandlingError) return;
        if (this.hasRecentReload()) {
            // Prevent continuous reload loop
            return;
        }
        
        this.isHandlingError = true;
        this.markReloadAttempt();

        // Show notification
        toast.info('Application has been updated. Reloading to get the latest version...', {
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
        });

        // Reload after a brief delay
        setTimeout(() => {
            this.reloadApplication();
        }, 2500);
    }

    hasRecentReload() {
        try {
            const lastReload = parseInt(localStorage.getItem('last-reload-ts') || '0', 10);
            if (!Number.isNaN(lastReload)) {
                return Date.now() - lastReload < this.reloadCooldownMs;
            }
        } catch (e) {
            // ignore
        }
        return false;
    }

    markReloadAttempt() {
        try {
            localStorage.setItem('last-reload-ts', Date.now().toString());
        } catch (e) {
            // ignore
        }
    }

    reloadApplication() {
        // Clear all possible caches
        if ('caches' in window) {
            caches.keys().then(names => {
                Promise.all(names.map(name => caches.delete(name)))
                    .then(() => {
                        console.log('All caches cleared');
                        this.performReload();
                    })
                    .catch(() => {
                        console.warn('Failed to clear some caches, proceeding with reload');
                        this.performReload();
                    });
            });
        } else {
            this.performReload();
        }
    }

    performReload() {
        // Clear localStorage items that might cause issues
        const keysToRemove = ['app-version', 'redux-persist'];
        keysToRemove.forEach(key => {
            try {
                localStorage.removeItem(key);
            } catch (e) {
                console.warn(`Failed to remove ${key} from localStorage:`, e);
            }
        });

        // Clear sessionStorage
        try {
            sessionStorage.clear();
        } catch (e) {
            console.warn('Failed to clear sessionStorage:', e);
        }

        // Force reload with cache bypass
        try {
            window.location.reload();
        } catch (e) {
            // Fallback for browsers that don't support the parameter
            try {
                window.location.replace(window.location.href);
            } catch (e2) {
                window.location.assign(window.location.href);
            }
        }
    }
}

// Create and initialize global error handler
const globalErrorHandler = new GlobalErrorHandler();

export default globalErrorHandler;
